package dev.lingvo.shared.screenshots.geom

import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.render.Affine
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin
import kotlin.math.sqrt

/** An axis-aligned rectangle; [w] and [h] are not negative for any rectangle this package produces. */
data class Rect(val x: Double, val y: Double, val w: Double, val h: Double) {
    val right: Double get() = x + w
    val bottom: Double get() = y + h

    /** True when [other] lies inside this rectangle, allowing [tolerance] on every edge. */
    fun contains(other: Rect, tolerance: Double = 0.0): Boolean =
        other.x >= x - tolerance && other.y >= y - tolerance &&
            other.right <= right + tolerance && other.bottom <= bottom + tolerance

    /** True when the two rectangles share any area (touching edges do not count). */
    fun intersects(other: Rect): Boolean =
        other.x < right && other.right > x && other.y < bottom && other.bottom > y
}

fun translate(x: Double, y: Double): Affine = Affine(1.0, 0.0, 0.0, 1.0, x, y)

fun scale(sx: Double, sy: Double): Affine = Affine(sx, 0.0, 0.0, sy, 0.0, 0.0)

/** Degrees clockwise on a y-down canvas, as `CanvasRenderingContext2D.rotate` with radians. */
fun rotate(degrees: Double): Affine {
    val radians = degrees * PI / 180.0
    val c = cos(radians)
    val s = sin(radians)
    return Affine(c, s, -s, c, 0.0, 0.0)
}

/** `(this * o)(p) == this(o(p))`: [o] applies first, as `ctx.transform(o)` does on top of the current matrix. */
operator fun Affine.times(o: Affine): Affine = Affine(
    a = a * o.a + c * o.b,
    b = b * o.a + d * o.b,
    c = a * o.c + c * o.d,
    d = b * o.c + d * o.d,
    e = a * o.e + c * o.f + e,
    f = b * o.e + d * o.f + f,
)

fun Affine.apply(p: Point): Point = Point(a * p.x + c * p.y + e, b * p.x + d * p.y + f)

/** Null when the matrix is singular (|det| < 1e-12). */
fun Affine.inverse(): Affine? {
    val det = a * d - b * c
    if (!det.isFinite() || abs(det) < 1e-12) return null
    return Affine(
        a = d / det,
        b = -b / det,
        c = -c / det,
        d = a / det,
        e = (c * f - d * e) / det,
        f = (b * e - a * f) / det,
    )
}

/**
 * The uniform scale this matrix applies to lengths, `sqrt(|a*d - b*c|)`. Canvas applies shadow offsets and blur in
 * device pixels regardless of the transform, so a renderer multiplies layer-space shadows by this first.
 */
fun Affine.lengthScale(): Double = sqrt(abs(a * d - b * c))

/** Box-local coordinates (origin top-left of the box) to parent coordinates: rotation about the centre, then flip. */
fun Box.toParent(): Affine =
    translate(x + w / 2, y + h / 2) * rotate(rotation) * scale(if (flipX) -1.0 else 1.0, 1.0) * translate(-w / 2, -h / 2)

/** The axis-aligned bounds of [box]'s four corners after [Box.toParent] and then [parent]. */
fun aabb(box: Box, parent: Affine = Affine.IDENTITY): Rect {
    val world = parent * box.toParent()
    val corners = listOf(Point(0.0, 0.0), Point(box.w, 0.0), Point(0.0, box.h), Point(box.w, box.h)).map { world.apply(it) }
    var minX = corners[0].x
    var maxX = corners[0].x
    var minY = corners[0].y
    var maxY = corners[0].y
    for (corner in corners) {
        minX = min(minX, corner.x)
        maxX = max(maxX, corner.x)
        minY = min(minY, corner.y)
        maxY = max(maxY, corner.y)
    }
    return Rect(minX, minY, maxX - minX, maxY - minY)
}
